import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

import { daysUntil } from '~/components/contractReview/contractDetailShared';
import { AiReviewUnavailableNote } from '~/components/contractReview/detailPage/AiReviewUnavailableNote';
import { ContractAskPanel } from '~/components/contractReview/detailPage/ContractAskPanel';
import { ContractIdentityFields } from '~/components/contractReview/detailPage/ContractIdentityFields';
import { ContractSummaryFields } from '~/components/contractReview/detailPage/ContractSummaryFields';
import type { ContractReview } from '~/components/contractReview/types';
import { BUTTON_SX, pageBandColor, pageBandDividerColor, SURFACE_RADIUS } from '~/theme';
import { useFeatureFlags } from '~/utils/featureFlags';

interface ContractDetailPageHeaderProps {
  contract: ContractReview | null;
  onBack: (() => void) | null;
  backLabel?: string;
  onOpenDocument: () => void;
  hasDocument: boolean;
  onEdit: (() => void) | null;
  compact?: boolean;
  footer?: ReactNode;
}

export function ContractDetailPageHeader({
  contract,
  onBack,
  backLabel,
  onOpenDocument,
  hasDocument,
  onEdit,
  compact = false,
  footer,
}: ContractDetailPageHeaderProps) {
  const { chatContract } = useFeatureFlags();

  const daysToRenewal = daysUntil(contract?.expirationDate);

  // A contract can have no expiration date (DateField(null=True)); show a plain
  // "No expiration date" chip rather than "Renews in NaN d" (AMP-585).
  const renewColor =
    daysToRenewal === null
      ? 'default'
      : daysToRenewal < 0
        ? 'error'
        : daysToRenewal <= 120
          ? 'warning'
          : 'default';
  const renewLabel =
    daysToRenewal === null
      ? 'No expiration date'
      : daysToRenewal < 0
        ? `Expired ${Math.abs(daysToRenewal)}d ago`
        : `Renews in ${daysToRenewal}d`;

  const titleBlock = (
    <>
      <Box display="flex" flexDirection="row" alignItems="center" gap={1} flexWrap="wrap">
        {contract ? (
          <>
            <Chip size="small" label={contract.type} variant="outlined" />
            <Chip
              size="small"
              color={renewColor}
              label={renewLabel}
              variant={renewColor === 'default' ? 'outlined' : 'filled'}
            />
          </>
        ) : (
          <Skeleton width={180} animation="wave" />
        )}
      </Box>
      <Typography variant="h5" noWrap>
        {contract ? (
          contract.name
        ) : (
          <Skeleton width={260} sx={{ fontSize: '1.5rem' }} animation="wave" />
        )}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5} ml={-0.5}>
        <Button
          size="small"
          startIcon={<DescriptionOutlinedIcon />}
          onClick={onOpenDocument}
          disabled={!hasDocument}
          sx={BUTTON_SX}
        >
          {hasDocument ? 'Show document' : 'No document'}
        </Button>
        {onEdit ? (
          <Button
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={onEdit}
            disabled={!contract}
            sx={BUTTON_SX}
          >
            Edit
          </Button>
        ) : null}
      </Box>
    </>
  );

  if (compact) {
    return (
      <Box px={2} py={2} sx={{ bgcolor: pageBandColor }}>
        <Stack gap="3px" alignItems="flex-start">
          {titleBlock}
        </Stack>
        <Box mt={2}>
          <ContractIdentityFields contract={contract} />
        </Box>
        {contract && !contract.aiReviewEligible ? (
          <Box mt={2}>
            <AiReviewUnavailableNote />
          </Box>
        ) : null}
        <Divider sx={{ my: 2, borderColor: pageBandDividerColor }} />
        <ContractSummaryFields contract={contract} stacked />
        {footer ? <Box mt={2}>{footer}</Box> : null}
      </Box>
    );
  }

  return (
    <Box
      px={{ xs: 2, md: 3 }}
      py={{ xs: 2, md: 3 }}
      sx={{
        bgcolor: pageBandColor,
        borderBottom: 1,
        borderColor: 'divider',
        borderTopLeftRadius: SURFACE_RADIUS,
        borderTopRightRadius: SURFACE_RADIUS,
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={3}>
        {onBack ? (
          <Box flexShrink={0} ml={-1}>
            <Tooltip title={backLabel ?? ''}>
              <IconButton onClick={onBack} aria-label={backLabel ?? 'Back'} size="small">
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
            <Stack gap="3px" alignItems="flex-start" minWidth={0} flexShrink={0}>
              {titleBlock}

              <Divider flexItem sx={{ mt: 1, borderColor: pageBandDividerColor }} />
            </Stack>

            {chatContract && contract ? (
              <Box
                minWidth={0}
                width={{ xs: '100%', lg: 'auto' }}
                sx={{ flex: { lg: '1 1 560px' }, maxWidth: { lg: 820 } }}
              >
                <ContractAskPanel contractId={contract.id} canChat={contract.canChat} />
              </Box>
            ) : null}
          </Box>

          <Box mt={{ xs: 2, md: 4 }}>
            <ContractIdentityFields contract={contract} />
          </Box>

          {contract && !contract.aiReviewEligible ? (
            <Box mt={2}>
              <AiReviewUnavailableNote />
            </Box>
          ) : null}

          <Divider sx={{ my: 2.5, borderColor: pageBandDividerColor }} />
          <ContractSummaryFields contract={contract} />
        </Box>
      </Box>
    </Box>
  );
}
